import classNames from 'classnames'
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { WemoloTypography } from '../Wemolo'
import styles from './TabsV2.module.css'

export type TabsV2Size = 'medium' | 'small'
export type TabsV2Width = 'hug' | 'hug-scroll' | 'full'

export interface TabsV2Item {
    key: string
    label: React.ReactNode
    content: React.ReactNode
    disabled?: boolean
    /** Defer first mount until the tab is opened, then keep it mounted (overrides `renderAll` for this tab). */
    lazy?: boolean
}

export interface TabsV2Props {
    tabs: TabsV2Item[]
    /** Controlled active tab key. Omit for uncontrolled mode. */
    activeKey?: string
    defaultActiveKey?: string
    onChange?: (key: string) => void
    /** Wrap tab changes (e.g. prompt for unsaved changes before switching). */
    confirm?: (next: () => void) => void
    size?: TabsV2Size
    width?: TabsV2Width
    /** Mount inactive panels so their state survives tab switches. */
    renderAll?: boolean
    className?: string
    classes?: {
        tabList?: string
        tab?: string
        panel?: string
    }
    'aria-label'?: string
}

const tabListClassByWidth: Record<TabsV2Width, string> = {
    hug: styles.tabListHug,
    'hug-scroll': styles.tabListHugScroll,
    full: styles.tabListFull
}

const tabSizeClass: Record<TabsV2Size, string> = {
    medium: styles.tabMedium,
    small: styles.tabSmall
}

const typographyVariantBySize: Record<TabsV2Size, 'label-medium' | 'label-small'> = {
    medium: 'label-medium',
    small: 'label-small'
}

let tabsV2InstanceCounter = 0
const nextTabsV2Id = () => `tabsv2-${++tabsV2InstanceCounter}`

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export const TabsV2: React.FC<TabsV2Props> = ({
    tabs,
    activeKey: controlledActiveKey,
    defaultActiveKey,
    onChange,
    confirm,
    size = 'medium',
    width = 'hug',
    renderAll = false,
    className,
    classes,
    'aria-label': ariaLabel
}) => {
    const baseIdRef = useRef<string>()
    if (!baseIdRef.current) baseIdRef.current = nextTabsV2Id()
    const baseId = baseIdRef.current
    const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
    const tabListRef = useRef<HTMLDivElement>(null)
    const indicatorRef = useRef<HTMLSpanElement>(null)
    const lastIndicatorPosRef = useRef<{ left: number; width: number } | null>(null)
    const prevActiveKeyRef = useRef<string | null>(null)
    const [direction, setDirection] = useState<1 | -1>(1)

    const firstEnabledKey = tabs.find((tab) => !tab.disabled)?.key ?? tabs[0]?.key ?? ''
    const [uncontrolledKey, setUncontrolledKey] = useState<string>(defaultActiveKey ?? firstEnabledKey)
    const isControlled = controlledActiveKey !== undefined
    const activeKey = isControlled ? controlledActiveKey : uncontrolledKey

    // Track keys that have been opened so `lazy` panels defer their first mount, then stay mounted.
    const [openedKeys, setOpenedKeys] = useState<Set<string>>(() => new Set(activeKey ? [activeKey] : []))
    useEffect(() => {
        setOpenedKeys((prev) => (prev.has(activeKey) ? prev : new Set(prev).add(activeKey)))
    }, [activeKey])

    const selectTab = useCallback(
        (nextKey: string) => {
            if (nextKey === activeKey) return
            const apply = () => {
                if (!isControlled) setUncontrolledKey(nextKey)
                onChange?.(nextKey)
            }
            if (confirm) {
                confirm(apply)
            } else {
                apply()
            }
        },
        [activeKey, confirm, isControlled, onChange]
    )

    const enabledKeys = tabs.filter((tab) => !tab.disabled).map((tab) => tab.key)

    const focusKey = (key: string) => {
        tabRefs.current.get(key)?.focus()
    }

    const measureAndAnimateIndicator = useCallback(
        (animate: boolean) => {
            const list = tabListRef.current
            const indicatorEl = indicatorRef.current
            const activeButton = tabRefs.current.get(activeKey)
            if (!list || !indicatorEl) return
            if (!activeButton) {
                indicatorEl.style.opacity = '0'
                return
            }
            const listRect = list.getBoundingClientRect()
            const buttonRect = activeButton.getBoundingClientRect()
            const targetLeft = buttonRect.left - listRect.left + list.scrollLeft
            const targetWidth = buttonRect.width

            indicatorEl.style.opacity = '1'

            const prev = lastIndicatorPosRef.current
            const shouldAnimate =
                animate &&
                !prefersReducedMotion() &&
                prev !== null &&
                (prev.left !== targetLeft || prev.width !== targetWidth) &&
                typeof indicatorEl.animate === 'function'

            // Commit the final inline styles immediately — animation visually overrides while playing.
            indicatorEl.style.transform = `translateX(${targetLeft}px)`
            indicatorEl.style.width = `${targetWidth}px`

            if (shouldAnimate && prev) {
                const stretchLeft = Math.min(prev.left, targetLeft)
                const stretchRight = Math.max(prev.left + prev.width, targetLeft + targetWidth)
                const stretchWidth = stretchRight - stretchLeft
                const travel = Math.abs(targetLeft + targetWidth / 2 - (prev.left + prev.width / 2))
                // Scale duration with travel distance so adjacent tabs feel snappier and far jumps feel weighty.
                const duration = Math.min(560, Math.max(320, 280 + travel * 0.45))

                indicatorEl.getAnimations().forEach((animation) => animation.cancel())
                indicatorEl.animate(
                    [
                        { transform: `translateX(${prev.left}px)`, width: `${prev.width}px` },
                        {
                            transform: `translateX(${stretchLeft}px)`,
                            width: `${stretchWidth}px`,
                            offset: 0.5
                        },
                        { transform: `translateX(${targetLeft}px)`, width: `${targetWidth}px` }
                    ],
                    {
                        duration,
                        easing: 'cubic-bezier(0.65, 0, 0.35, 1)'
                    }
                )
            }

            lastIndicatorPosRef.current = { left: targetLeft, width: targetWidth }
        },
        [activeKey]
    )

    useLayoutEffect(() => {
        const isFirstMeasure = prevActiveKeyRef.current === null
        if (!isFirstMeasure && prevActiveKeyRef.current !== activeKey) {
            const nextIndex = tabs.findIndex((tab) => tab.key === activeKey)
            const prevIndex = tabs.findIndex((tab) => tab.key === prevActiveKeyRef.current)
            if (nextIndex !== -1 && prevIndex !== -1 && nextIndex !== prevIndex) {
                setDirection(nextIndex > prevIndex ? 1 : -1)
            }
        }
        prevActiveKeyRef.current = activeKey
        measureAndAnimateIndicator(!isFirstMeasure)
    }, [activeKey, tabs.length, size, width, measureAndAnimateIndicator])

    useEffect(() => {
        if (typeof ResizeObserver === 'undefined') return
        const list = tabListRef.current
        if (!list) return
        const observer = new ResizeObserver(() => {
            measureAndAnimateIndicator(false)
        })
        observer.observe(list)
        return () => observer.disconnect()
    }, [measureAndAnimateIndicator])

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (enabledKeys.length === 0) return
        const currentIndex = enabledKeys.indexOf(activeKey)
        const lastIndex = enabledKeys.length - 1

        let nextIndex: number | null = null
        switch (event.key) {
            case 'ArrowRight':
                nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % enabledKeys.length
                break
            case 'ArrowLeft':
                nextIndex = currentIndex < 0 ? lastIndex : (currentIndex - 1 + enabledKeys.length) % enabledKeys.length
                break
            case 'Home':
                nextIndex = 0
                break
            case 'End':
                nextIndex = lastIndex
                break
            default:
                return
        }
        event.preventDefault()
        const nextKey = enabledKeys[nextIndex]
        selectTab(nextKey)
        focusKey(nextKey)
    }

    return (
        <div className={classNames(styles.root, className)} style={{ ['--tabsv2-dir' as never]: direction }}>
            <div
                ref={tabListRef}
                role="tablist"
                aria-label={ariaLabel}
                className={classNames(styles.tabList, tabListClassByWidth[width], classes?.tabList)}
            >
                {tabs.map((tab) => {
                    const isActive = tab.key === activeKey
                    const tabId = `${baseId}-tab-${tab.key}`
                    const panelId = `${baseId}-panel-${tab.key}`
                    return (
                        <button
                            key={tab.key}
                            ref={(el) => {
                                if (el) {
                                    tabRefs.current.set(tab.key, el)
                                } else {
                                    tabRefs.current.delete(tab.key)
                                }
                            }}
                            id={tabId}
                            role="tab"
                            type="button"
                            aria-selected={isActive}
                            aria-controls={panelId}
                            tabIndex={isActive ? 0 : -1}
                            disabled={tab.disabled}
                            onClick={() => selectTab(tab.key)}
                            onKeyDown={handleKeyDown}
                            className={classNames(
                                styles.tab,
                                tabSizeClass[size],
                                isActive && styles.tabActive,
                                width === 'full' && styles.tabFull,
                                classes?.tab
                            )}
                        >
                            <span className={styles.tabLabel}>
                                <WemoloTypography variant={typographyVariantBySize[size]} tag="span">
                                    {tab.label}
                                </WemoloTypography>
                            </span>
                        </button>
                    )
                })}
                <span ref={indicatorRef} aria-hidden className={styles.indicator} style={{ opacity: 0 }} />
            </div>
            {tabs.map((tab) => {
                const isActive = tab.key === activeKey
                // Lazy tabs mount once opened and stay mounted; non-lazy tabs follow `renderAll`.
                const shouldMount = tab.lazy ? openedKeys.has(tab.key) : isActive || renderAll
                if (!shouldMount) return null
                const tabId = `${baseId}-tab-${tab.key}`
                const panelId = `${baseId}-panel-${tab.key}`
                return (
                    <div
                        key={tab.key}
                        id={panelId}
                        role="tabpanel"
                        aria-labelledby={tabId}
                        hidden={!isActive}
                        className={classNames(styles.panel, classes?.panel)}
                    >
                        {tab.content}
                    </div>
                )
            })}
        </div>
    )
}
